<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import TwoPanePage from "@/templates/TwoPanePage.vue";
  import { ChartLine, Rows3, Table2 } from "lucide-vue-next";
  import type { MetricsView } from "@stores/useCampaignsPivotStore";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { PIVOT_METRICS } from "./pivotMetrics";
  import { Spinner } from "@/components/ui/spinner";
  import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
  import { useCampaignsPivotStore } from "@stores/useCampaignsPivotStore";
  import { usePivotUrlSync } from "@/utils/composables/usePivotUrlSync";
  import PivotLeftPanel from "./components/PivotLeftPanel.vue";
  import PivotTable from "./components/PivotTable.vue";
  import MetricsPivotTable from "./components/MetricsPivotTable.vue";
  import MetricsColumnPicker from "./components/MetricsColumnPicker.vue";
  import MetricLineChart from "./components/MetricLineChart.vue";
  import { isTemporalDimension } from "./pivotDimensions";

  const store = useCampaignsPivotStore();
  const {
    hasScope, pivotData, metricsData, pivotMode, metricsView, selectedMetrics, activeRowDimensions, activeColumnDimensions, loading, error,
  } = storeToRefs(store);

  const visibleMetrics = computed(() =>
    PIVOT_METRICS.filter(
      (m) => selectedMetrics.value.includes(m.key) && (metricsData.value?.multiSurvey || !m.multiSurvey),
    ),
  );

  // Three readings of the same data. Split needs column buckets and several
  // metrics; lines need calendar buckets down the rows (columns become the series). An unavailable pick falls back
  // to the combined table without being lost.
  const canSplit = computed(() => activeColumnDimensions.value.length > 0 && visibleMetrics.value.length > 1);
  const canChart = computed(() => activeRowDimensions.value.length > 0 && activeRowDimensions.value.every(isTemporalDimension));

  const VIEWS: Array<{ value: MetricsView; label: string; icon: typeof Table2; available: () => boolean; hint: string }> = [
    { value: "combined", label: "All metrics in one table", icon: Table2, available: () => true, hint: "" },
    { value: "split", label: "One table per metric", icon: Rows3, available: () => canSplit.value, hint: "Needs a column dimension and several metrics" },
    { value: "chart", label: "One line chart per metric", icon: ChartLine, available: () => canChart.value, hint: "Needs Year, Quarter, Month or Day as row dimensions" },
  ];

  const effectiveView = computed<MetricsView>(() => {
    const view = VIEWS.find((v) => v.value === metricsView.value);
    return view?.available() ? view.value : "combined";
  });
  const isCharted = computed(() => effectiveView.value === "chart");
  const isStacked = computed(() => effectiveView.value !== "combined");

  usePivotUrlSync();
</script>

<template>
  <div class="px-1 pb-4 h-full flex flex-col">
    <TwoPanePage
      :span-left="3"
      :span-right="9"
      class="flex-1 min-h-0 items-start">
      <template #left>
        <PivotLeftPanel />
      </template>

      <template #right>
        <div
          class="flex flex-col gap-3 h-full"
          :class="isStacked ? 'overflow-y-auto max-h-[calc(100vh-40px)] pr-1' : ''">
          <div class="flex items-center justify-between gap-3">
            <Tabs
              :model-value="pivotMode"
              @update:model-value="pivotMode = $event as typeof pivotMode">
              <TabsList>
                <TabsTrigger value="metrics">Metrics</TabsTrigger>
                <TabsTrigger value="groups">Groups CPI</TabsTrigger>
              </TabsList>
            </Tabs>

            <div class="flex items-center gap-2 shrink-0">
              <div
                v-if="pivotMode === 'metrics'"
                class="inline-flex items-center rounded-md border bg-white p-0.5">
                <Tooltip
                  v-for="view in VIEWS"
                  :key="view.value"
                  :content="view.available() ? view.label : `${view.label} — ${view.hint}`"
                  side="bottom">
                  <span class="inline-flex">
                    <Button
                      :variant="effectiveView === view.value ? 'secondary' : 'ghost'"
                      size="icon-sm"
                      class="size-7"
                      :disabled="!view.available()"
                      @click="metricsView = view.value">
                      <component
                        :is="view.icon"
                        class="size-4" />
                    </Button>
                  </span>
                </Tooltip>
              </div>
              <MetricsColumnPicker v-if="pivotMode === 'metrics'" />
            </div>
          </div>
          <div
            v-if="error"
            class="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {{ error }}
          </div>

          <template v-if="pivotMode === 'metrics'">
            <template v-if="metricsData && !loading">
              <template v-if="isStacked">
                <div
                  v-for="metric in visibleMetrics"
                  :key="metric.key"
                  class="flex flex-col gap-1">
                  <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">{{ metric.label }}</p>
                  <MetricLineChart
                    v-if="isCharted"
                    :data="metricsData"
                    :metric="metric" />
                  <MetricsPivotTable
                    v-else
                    :data="metricsData"
                    :only="[metric.key]" />
                </div>
              </template>
              <MetricsPivotTable
                v-else
                :data="metricsData" />
            </template>
            <div
              v-else-if="loading"
              class="flex h-64 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Spinner class="size-4" />
              Loading...
            </div>
            <div
              v-else-if="!hasScope"
              class="flex h-64 flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
              <span>Select a survey or an account to get started</span>
              <span class="text-xs">or filter on MCampaign status to see live campaigns across accounts</span>
            </div>
          </template>

          <template v-else>
            <PivotTable
              v-if="pivotData && !loading"
              :data="pivotData" />
            <div
              v-else-if="loading"
              class="flex h-64 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Spinner class="size-4" />
              Loading...
            </div>
            <div
              v-else-if="!hasScope"
              class="flex h-64 items-center justify-center text-sm text-muted-foreground">
              Select an account or a survey to get started
            </div>
          </template>
        </div>
      </template>
    </TwoPanePage>
  </div>
</template>
